import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAction, useAdmin, useTitle } from '../lib';
import { Alert, Button, Field } from '../ui';

/** Password first, then an authenticator code. A first sign-in sets up the authenticator with a QR code. */
export function Login() {
  useTitle('Sign in');
  const { admin, refresh } = useAdmin();
  const [f, setF] = useState({ email: '', password: '' });
  const [next, setNext] = useState<'totp' | 'setup' | null>(admin?.stage === 'password' ? (admin.totpEnabled ? 'totp' : 'setup') : null);
  const login = useAction(async () => {
    const r = await api.post<{ next: 'totp' | 'setup' }>('/admin/login', f);
    setNext(r.next);
  });

  return (
    <main id="main" className="wrap">
      <div className="card stack auth-card">
        <h1 style={{ fontSize: '1.6rem' }}>Math Ladder Admin</h1>
        {!next && (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void login.run(); }}>
            <Field label="Email" type="email" autoComplete="username" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <Field label="Password" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            <Alert>{login.error}</Alert>
            <Button className="btn btn-primary" busy={login.busy}>Continue</Button>
          </form>
        )}
        {next && <Totp setup={next === 'setup'} onDone={refresh} onRestart={() => setNext(null)} />}
      </div>
    </main>
  );
}

function Totp({ setup, onDone, onRestart }: { setup: boolean; onDone: () => Promise<void>; onRestart: () => void }) {
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<{ secret: string; qr: string } | null>(null);
  const start = useAction(async () => setQr(await api.post<{ secret: string; qr: string }>('/admin/totp/setup')));
  const verify = useAction(async () => { await api.post('/admin/totp/verify', { code }); await onDone(); });
  useEffect(() => { if (setup) void start.run(); }, [setup]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); void verify.run(); }}>
      {setup ? (
        <>
          <p style={{ margin: 0 }}>Two-step sign-in is required. Scan this code with an authenticator app (1Password, Google Authenticator, Authy), then enter the 6-digit code it shows.</p>
          <Alert>{start.error}</Alert>
          {qr && (
            <div className="stack" style={{ alignItems: 'center' }}>
              <img src={qr.qr} width={220} height={220} alt="Authenticator QR code" style={{ background: '#fff', padding: 8, borderRadius: 8 }} />
              <div className="small muted">Or enter this key: <span className="mono">{qr.secret}</span></div>
            </div>
          )}
        </>
      ) : (
        <p style={{ margin: 0 }}>Enter the 6-digit code from your authenticator app.</p>
      )}
      <Field label="Code" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
      <Alert>{verify.error}</Alert>
      <Button className="btn btn-primary" busy={verify.busy}>Sign in</Button>
      <button type="button" className="btn btn-quiet btn-sm" onClick={onRestart}>Start over</button>
    </form>
  );
}
